import React from 'react'
import { ReactSVG } from 'react-svg';
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { v4 as genId } from 'uuid'
import { Components, Types } from '../../types'
import { MobileMenuContext } from '../../contexts/MobileMenu';
import Menu from './Menu'
import Toggler from './Toggler'
import _ from 'lodash'
import { Helpers } from '../../utils';

/**
 * A menu Item component to render one list element
 * @param param0
 * @returns
 */
const Item: React.FC<Components.MobileMenuItem> = ({
  permalink,
  items,
  label,
  config,
  theme,
  icon,
  hideIconInMobileMenu
}) => {

  // Actions handlers
  const { handleNext, handleReset, handleBeforeReset } = React.useContext(MobileMenuContext)

  // Has an item any subitems? 
  const hasSubItems: boolean = !_.isEmpty(items);

  // Submenu ID to be passed to the child ul
  const subMenuIdRef = React.useRef(genId())
  const subMenuId = subMenuIdRef.current

  // URL pathname
  const pathname = config.general.useRouter ? new URL(permalink)?.pathname : permalink;
  const options = {
    config: config,
    theme: theme,
  }

  const Linked: React.FC<any> = ({ useRouter, to, ...props }) => {
    return useRouter ? <Link to={to} {...props} /> : <a href={to} {...props}>{props.children}</a>
  }

  const classes = `${hasSubItems ? 'parent' : ''}`

  return (
    <StyledItem {...options} className={classes}>
      {config.mobileMenu.treatAsLink ? (
        // Linkable Item line
        <Linked to={pathname} onClick={() => handleBeforeReset(config.general.timingBase)} useRouter={config.general.useRouter}>
          <StyledGrid {...options}>
            <StyledName {...options}>
              {(!_.isUndefined(icon) && icon && !hideIconInMobileMenu) && <ReactSVG className="item-icon" src={icon} />}
              <span>
                {label}
              </span>
            </StyledName>
            {/* User clicked on the next handler */}
            {hasSubItems && (
              <StyledToggler onClick={(event) => {
                if (hasSubItems) {
                  // Prevent link behavior
                  event.preventDefault()
                  event.stopPropagation()
                  // Go deeper
                  handleNext(subMenuId)
                }
              }}>
                <Toggler iconId={'angleRight'} {...options} />
              </StyledToggler>
            )}
          </StyledGrid>
        </Linked>
      ) : (
        // Linkable Item line
        <Linked
          to={pathname}
          useRouter={config.general.useRouter}
          onClick={(event: Event) => {
            if (hasSubItems) {
              // Prevent Link behavior
              event.preventDefault()
              // Go deeper
              handleNext(subMenuId)
            } else {
              // Close the menu, changed the view
              handleBeforeReset(config.general.timingBase)
            }
          }}
        >
          <StyledGrid pointer {...options}>
            <StyledName>{label}</StyledName>
            {hasSubItems && (
              <StyledToggler>
                <Toggler iconId={'angleRight'} {...options}  />
              </StyledToggler>
            )}
          </StyledGrid>
        </Linked>
      )}
      {/* Item divider */}
      <StyledDivider {...options} />
      {/* Submenu */}
      {hasSubItems && items && (
        <Menu
          items={items}
          {...options}
          forwardedRef={subMenuIdRef}
          forwardedTitle={label || label}
        />
      )}
    </StyledItem>
  )
}

const StyledItem = styled.li<Types.AppOptions>`
  ${({ config: c, theme: t }) => `
  ${!c.general.turnOffTransitions && t.hoverTransition
      ? `transition: background ${t.hoverTransition}ms ${c.general.transitionFunction};`
      : ''
    }

  &:hover {
    background: ${t.mobileMenu.itemHoverBackground};
  }

  &:hover {
    background: ${t.mobileMenu.itemHoverBackground};
  }

  & a {
    text-decoration: none;
    display: block;
    height: 100%;
    line-height: 1.5em;
    color: ${t.mobileMenu.textColor};
  }
`}
`

// Extended interface for the StyledGrid
interface IStyledGrid extends Types.AppOptions {
  padded?: boolean
  pointer?: boolean
  onClick?: () => void
}

// Grid-based Item line
const StyledGrid = styled.div<IStyledGrid>`
  ${(p) => `
  display: grid;
  grid-template-areas:
    'Name Toggler'
    'Divider Divider';
  grid-template-columns: 1fr min-content;
  grid-column-gap: 1em;
  min-height: 2.25em;
  align-items: center;
  align-content: center;
  ${p.padded ? `padding: 0 0.5em;` : ''}
  ${p.pointer ? `cursor: pointer;` : ''}
`}
`

const StyledName = styled.div<any>`
  grid-area: Name;
  display: flex;
    align-items: center;

    .item-icon {
		width: 1.75em;
		height: 1.75em;
		margin-right: 0.5em;
		svg {
			width: 100%;
			height: 100%;
		}
	}
  ${({theme}) => theme.mobileMenu.textSize === 'inherit' 
    ? `font-size: ${theme.mobileMenu.textSize}`
    : Helpers.clamp('font-size', 0.95, theme.mobileMenu.textSize, 4.25)
  }; // Jungle - X Ambassadors

  padding: 0.75em 0 0.75em 1.5em;
`

const StyledToggler = styled.div`
  grid-area: Toggler;
  padding: 0.75em 1.5em 0.75em 0;
`

// Customizable divider
const StyledDivider = styled.div<Types.AppOptions>`
  ${(p) => `
  grid-area: Divider;
  height: ${p.theme.mobileMenu.dividerSize}em;
  background: ${p.theme.mobileMenu.dividerBackground};
`}
`

export default Item
